import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Search, Edit, Trash2, MoreHorizontal, Users, GripVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ORDER = ['General Partners', 'Venture Partners', 'Team', 'Mentors and Advisors'];

// team_type keys whose display name isn't just the key with spaces
const TEAM_TYPE_DISPLAY = { founders_office: "MD's Office" };

function getCategoryPriority(member) {
  const teamType = member.team_type || '';
  const memberType = (member.member_type || '').toLowerCase();
  if (teamType === 'general_partners') return 0;
  if (teamType === 'venture_partners') return 1;
  if (teamType === 'advisors' || memberType === 'advisor' || memberType === 'mentor') return 3;
  return 2;
}

function groupMembersByCategory(members) {
  const grouped = {};
  CATEGORY_ORDER.forEach((_, i) => { grouped[CATEGORY_ORDER[i]] = []; });
  const sorted = [...members].sort((a, b) => {
    const pa = getCategoryPriority(a);
    const pb = getCategoryPriority(b);
    if (pa !== pb) return pa - pb;
    const oa = a.display_order ?? 0;
    const ob = b.display_order ?? 0;
    if (oa !== ob) return oa - ob;
    return (a.name || '').localeCompare(b.name || '');
  });
  sorted.forEach((m) => {
    const p = getCategoryPriority(m);
    grouped[CATEGORY_ORDER[p]].push(m);
  });
  return grouped;
}

function SortableRow({ member, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.member_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? 'opacity-50 bg-muted' : ''}>
      <TableCell className="w-10 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          <img
            src={member.photo_url ? (member.photo_url.startsWith('/') ? `${BACKEND_URL}${member.photo_url}` : member.photo_url) : `https://ui-avatars.com/api/?name=${member.name}`}
            alt={member.photo_alt || member.name}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div>
            <p className="font-medium">{member.name}</p>
            {member.linkedin_url && <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600">LinkedIn</a>}
          </div>
        </div>
      </TableCell>
      <TableCell><Badge variant="outline" className="capitalize">{member.member_type?.replace('_', ' ')}</Badge></TableCell>
      <TableCell>{member.designation}</TableCell>
      <TableCell className="capitalize">{TEAM_TYPE_DISPLAY[member.team_type] ?? member.team_type?.replace('_', ' ')}</TableCell>
      <TableCell><Badge variant={member.is_active ? 'default' : 'secondary'}>{member.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(member.member_id)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDelete(member.member_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

const TeamList = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/team?include_inactive=true`, { withCredentials: true });
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/team/${deleteId}`, { withCredentials: true });
      toast.success('Team member deleted');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const grouped = groupMembersByCategory(filteredMembers);
    let sectionMembers = null;
    let categoryName = null;
    for (const name of CATEGORY_ORDER) {
      const list = grouped[name] || [];
      const idx = list.findIndex((m) => m.member_id === active.id);
      if (idx >= 0) {
        sectionMembers = list;
        categoryName = name;
        break;
      }
    }
    if (!sectionMembers || sectionMembers.length <= 1) return;
    const oldIndex = sectionMembers.findIndex((m) => m.member_id === active.id);
    const newIndex = sectionMembers.findIndex((m) => m.member_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sectionMembers, oldIndex, newIndex);
    const memberIds = reordered.map((m) => m.member_id);
    setReordering(true);
    try {
      await axios.post(`${API}/admin/team/reorder`, { member_ids: memberIds }, { withCredentials: true });
      toast.success('Order updated');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setReordering(false);
    }
  };

  const filteredMembers = members.filter((m) => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = memberTypeFilter === 'all' || m.member_type === memberTypeFilter;
    return matchesSearch && matchesType;
  });

  const grouped = groupMembersByCategory(filteredMembers);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-gray-500">Drag rows to change display order within each section</p>
        </div>
        <Button onClick={() => navigate('/admin/team/new')} className="bg-[#C8473A] hover:bg-[#B74F40]">
          <Plus className="w-4 h-4 mr-2" /> Add Member
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={memberTypeFilter} onValueChange={setMemberTypeFilter}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Filter by Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="partner">Partner</SelectItem>
            <SelectItem value="advisor">Advisor</SelectItem>
            <SelectItem value="mentor">Mentor</SelectItem>
            <SelectItem value="employee">Employee</SelectItem>
            <SelectItem value="consultant">Consultant</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="space-y-8">
          {CATEGORY_ORDER.map((cat) => {
            const sectionMembers = grouped[cat] || [];
            if (sectionMembers.length === 0) return null;
            const ids = sectionMembers.map((m) => m.member_id);
            return (
              <div key={cat} className="bg-white rounded-lg border">
                <h2 className="text-lg font-semibold px-4 py-3 border-b bg-muted/50">{cat}</h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Member</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                      {sectionMembers.map((member) => (
                        <SortableRow
                          key={member.member_id}
                          member={member}
                          onEdit={(id) => navigate(`/admin/team/${id}`)}
                          onDelete={setDeleteId}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </div>
            );
          })}
        </div>

        {!loading && filteredMembers.length === 0 && (
          <div className="bg-white rounded-lg border flex justify-center items-center py-12">
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No members found</p>
            </div>
          </div>
        )}
      </DndContext>

      {reordering && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg px-6 py-4 shadow-lg">Updating order…</div>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Member</AlertDialogTitle>
            <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamList;
